import { Component, StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { I18nProvider, useI18n, type Lang } from "./i18n";
import { Icon, UiProvider } from "./ui";
import { HelpHost, LinkHost, Manual, PageHead, Router, SessionProvider, Shell, match, useRouter } from "./app";
import { scopeOf } from "./perm";
import { httpApi, type Api } from "./api/api";
import type { Me } from "./api/types";
import { Home, Login } from "./pages/home";
import { Directory, Import, MyProfile, Org } from "./pages/people";
import { Calendar, Daily, Devices, OffDays, Exceptions, LeaveApprovals, Month, MyLeave, Overtime } from "./pages/time";
import { MyPayslips, RunDetail, Runs, SalaryGrid, Tax } from "./pages/payroll";
import { Billing, Organisation, Branches, Company, Holidays, Messaging, Modules, Policies, Preferences, Roles } from "./pages/settings";
import { Careers, ResetPassword } from "./pages/public";
import { PlatformApp } from "./pages/platform";
import { Assets, Lifecycle, Notices, Recruitment, Reviews, Trainings } from "./pages/more";
import { FieldLive, FieldRoute, FieldSettings, MyFieldDay, PunchLog } from "./pages/field";
import { appTokenStore, isApp } from "./native";
import { appAllows } from "./appmode";
import { setRefreshStore } from "./api/api";

declare const __DEMO__: boolean;
const demoMod = __DEMO__ ? await import("./api/demo") : null;
const api: Api = demoMod ? demoMod.demoApi : httpApi;
if (isApp) setRefreshStore(appTokenStore); // the app keeps its own session token

function Routes() {
  const { path } = useRouter();
  const p = (pat: string) => match(pat, path);
  if (isApp && !appAllows(path)) return <WebOnly />;
  if (path === "/") return <Home />;
  if (path === "/time/punches") return <PunchLog />;
  if (path === "/time/field-day") return <MyFieldDay />;
  if (path === "/time/field") return <FieldLive />;
  if (p("/time/field/:id")) return <FieldRoute id={p("/time/field/:id")!.id} />;
  if (path === "/settings/mobile") return <FieldSettings />;
  if (path === "/people") return <Directory />;
  if (path === "/people/org") return <Org />;
  if (path === "/people/import") return <Import />;
  if (path === "/people/me") return <MyProfile />;
  if (p("/people/:id")) return <Directory openId={p("/people/:id")!.id} />;
  if (path === "/time") return <Daily />;
  if (path === "/time/month") return <Month />;
  if (path === "/time/exceptions") return <Exceptions />;
  if (path === "/time/ot") return <Overtime />;
  if (path === "/time/leave") return <LeaveApprovals />;
  if (path === "/time/my-leave") return <MyLeave />;
  if (path === "/time/calendar") return <Calendar />;
  if (path === "/time/devices") return <Devices />;
  if (path === "/payroll") return <Runs />;
  if (p("/payroll/run/:id")) return <RunDetail id={p("/payroll/run/:id")!.id} />;
  if (path === "/payroll/salary") return <SalaryGrid />;
  if (path === "/payroll/tax") return <Tax />;
  if (path === "/payroll/mine") return <MyPayslips />;
  if (path === "/work/notices") return <Notices />;
  if (path === "/work/recruitment") return <Recruitment />;
  if (path === "/work/reviews") return <Reviews />;
  if (path === "/work/lifecycle") return <Lifecycle />;
  if (path === "/work/training") return <Trainings />;
  if (path === "/work/assets") return <Assets />;
  if (path === "/settings") return <Company />;
  if (path === "/settings/branches") return <Branches />;
  if (path === "/settings/organisation") return <Organisation />;
  if (path === "/settings/policies") return <Policies />;
  if (path === "/settings/holidays") return <Holidays />;
  if (path === "/settings/messaging") return <Messaging />;
  if (path === "/settings/roles") return <Roles />;
  if (path === "/settings/modules") return <Modules />;
  if (path === "/settings/preferences") return <Preferences />;
  if (path === "/settings/billing" || path === "/billing") return <Billing />;
  if (path === "/help") return <HelpPage />;
  if (path === "/time/offdays") return <OffDays />;
  return <Home />;
}

/** One broken screen must not blank the whole portal. */
class Boundary extends Component<{ children: ReactNode; label: string }, { err: string | null }> {
  state = { err: null as string | null };
  static getDerivedStateFromError(e: Error) { return { err: e.message }; }
  componentDidCatch(e: Error) { console.error(e); }
  render() { return this.state.err ? <div className="panel"><div className="panel-b"><div className="note bad"><Icon n="alert" /><span>{this.props.label} <code className="xs">{this.state.err}</code></span></div></div></div> : this.props.children; }
}

/** In the Android app, critical work (payroll, salary, roles, settings…) is done in the web portal. */
function WebOnly() { const { t } = useI18n(); const { go } = useRouter();
  return <div className="panel app-only-note"><div className="panel-b stack" style={{ textAlign: "center" }}><Icon n="device" /><h2 style={{ margin: 0 }}>{t("Use the web portal for this")}</h2>
    <p className="muted" style={{ margin: 0 }}>{t("Payroll, salary, roles, company settings, devices and employee records are managed from a computer, in the web portal.")}</p>
    <button className="btn" onClick={() => go("/")}>{t("Back to home")}</button></div></div>; }
function HelpPage() { const { t } = useI18n(); return <div style={{ maxWidth: 860 }}><PageHead title={t("User manual")} sub={t("Step-by-step help for every screen. The Guide button on each page opens the part for that page.")} /><Manual filterGroup={(g) => g !== "platform"} /></div>; }

type Mode = "tenant" | "platform";
function App() {
  const { t, setLang } = useI18n(); const { path, go } = useRouter();
  const [mode, setMode] = useState<Mode>(() => (path.startsWith("/platform") ? "platform" : "tenant"));
  const [me, setMe] = useState<Me | null>(null), [boot, setBoot] = useState(true), [platformIn, setPlatformIn] = useState(false), [counts, setCounts] = useState<{ leave?: number }>({}), [tick, setTick] = useState(0);
  const load = useCallback(async () => { try { const m = await api.auth.me(); setMe(m); if (!localStorageLang()) setLang(m.user.lang); } catch { setMe(null); } finally { setBoot(false); } }, []); // eslint-disable-line
  useEffect(() => { if (mode === "tenant") load(); else setBoot(false); }, [mode, load, tick]);
  useEffect(() => { if (me && path === "/login") go("/"); }, [me, path, go]); // signed in: /login is only the sign-in address
  useEffect(() => { if (!me) return; const can = scopeOf(me.permissions, "leave.approve"); if (can) api.leave.requests({ waitingForMe: true }).then((r) => setCounts({ leave: r.length }), () => {}); }, [me, path]);
  const session = useMemo(() => me && { api, me, can: (k: string) => !!scopeOf(me.permissions, k), scope: (k: string) => scopeOf(me.permissions, k), mod: (k: string) => !!me.modules.find((m) => m.key === k)?.enabled, reload: load,
    logout: async () => { const imp = me.impersonation; await api.auth.logout().catch(() => {}); if (imp) { await api.platform.endImpersonation(imp.id).catch(() => {}); setMe(null); setMode("platform"); go("/platform/tenants"); } else { setMe(null); go("/login"); } } }, [me, load]); // eslint-disable-line

  const demoBar = demoMod && <DemoBar mode={mode} onMode={(m) => { setMode(m); go(m === "platform" ? "/platform" : "/"); }} onRole={(r) => { demoMod.demoSession.setRole(r); setTick((x) => x + 1); go("/"); }} />;
  const pub = match("/careers/:slug", path);
  if (pub) return <Careers api={api} slug={pub.slug} />;
  if (path === "/reset") { const q = new URLSearchParams(location.search); return <ResetPassword api={api} company={q.get("c") ?? ""} token={q.get("t") ?? ""} onDone={() => go("/login")} />; }
  if (boot) return <div className="empty">{t("Loading…")}</div>;
  if (mode === "platform") {
    if (!platformIn) return <>{demoBar}<Login api={api} platform onDone={() => { setPlatformIn(true); go("/platform"); }} /></>;
    return <>{demoBar}<PlatformApp api={api} onLogout={() => setPlatformIn(false)} onImpersonate={() => { setMode("tenant"); setTick((x) => x + 1); go("/"); }} /></>;
  }
  if (!me || !session) return <>{demoBar}<Login api={api} onDone={() => { setBoot(true); load(); }} /></>;
  return <SessionProvider value={session}><Shell counts={counts} demoBar={demoBar}><Boundary key={path + tick} label={t("This screen couldn't be shown. Reload the page; if it happens again, tell support.")}><Routes key={me.roles[0] + tick} /></Boundary></Shell></SessionProvider>;
}
const localStorageLang = () => { try { return localStorage.getItem("kormi:lang"); } catch { return null; } };

function DemoBar({ mode, onMode, onRole }: { mode: Mode; onMode: (m: Mode) => void; onRole: (r: any) => void }) {
  const { t } = useI18n(); const s = demoMod!.demoSession.get();
  return <div className="banner-demo" role="note"><div className="row" style={{ justifyContent: "center", gap: 10 }}>
    <span><Icon n="info" className="ico" /> <b>{t("Preview")}</b> · {t("sample data in your browser; nothing is saved or sent")}</span>
    <span className="seg"><button aria-pressed={mode === "tenant"} onClick={() => onMode("tenant")}>{t("Company portal")}</button><button aria-pressed={mode === "platform"} onClick={() => onMode("platform")}>{t("Super Admin")}</button></span>
    {mode === "tenant" && s.loggedIn && !s.impersonating && <label className="row small">{t("View as")}<select className="select" style={{ width: "auto", padding: "3px 8px" }} value={s.role} onChange={(e) => onRole(e.target.value)}>
      {[["TENANT_ADMIN", "Company admin"], ["HR_MANAGER", "HR manager"], ["PAYROLL_MANAGER", "Payroll manager"], ["LINE_MANAGER", "Line manager"], ["EMPLOYEE", "Employee"], ["FIELD_STAFF", "Field staff (sales)"], ["OFFICE_STAFF", "Office staff (web punch)"]].map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</select></label>}
  </div></div>;
}

const initialLang: Lang = (localStorageLang() as Lang) || "bn";
createRoot(document.getElementById("root")!).render(<StrictMode><I18nProvider initial={initialLang}><UiProvider><Router memory={!!demoMod || isApp}><App /><LinkHost /><HelpHost /></Router></UiProvider></I18nProvider></StrictMode>);
